import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/* eslint-disable no-console */
import React, { useState, useRef, forwardRef, useImperativeHandle } from "react";
import { Form, VerticalSpacing, getClassNameFactory, getRehydratableName, Typography, Sizes } from "@dcl/ui";
import Dropdown from "../../modules/Dropdown";
import { Toast, ToastType } from "../Toast";
import { TypographyTags } from "@dcl/ui/esm/elements/Typography";
import { propsToDataAttributes } from "@tr-digital/react-rehydrate";
import { REQUIRED } from "./constants";
import ErrorIcon from "./utils/ErrorIcon";
import { CascadingDropdown } from "../CascadingDropdown";
export var DynamicForm = /*#__PURE__*/forwardRef(function (props, ref) {
  var formFields = props.formFields;
  var getClassName = getClassNameFactory(DynamicForm.displayName);
  var initialState = formFields === null || formFields === void 0 ? void 0 : formFields.reduce(function (acc, field) {
    acc[field.name] = {
      value: field.fieldType === "checkBox" ? false : "",
      error: ""
    };
    return acc;
  }, {});

  var _useState = useState(initialState),
      _useState2 = _slicedToArray(_useState, 2),
      formState = _useState2[0],
      setFormState = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      apiError = _useState4[0],
      setApiError = _useState4[1];

  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var NAME_PATTERN = /^[a-zA-Z\s]+$/;
  var validators = {
    email: function email(value) {
      if (!value) {
        return "";
      }

      return EMAIL_PATTERN.test(value) ? "" : "Invalid email address";
    },
    name: function name(value) {
      if (!value) {
        return "";
      }

      return NAME_PATTERN.test(value) ? "" : "Invalid name";
    }
  };

  var validateField = function validateField(name, value) {
    var validator = validators[name];
    return validator ? validator(value) : "";
  };

  var handleChange = function handleChange(formInput) {
    var _formInput$target = formInput.target,
        name = _formInput$target.name,
        value = _formInput$target.value;
    var ariaRequired = formInput.target.getAttribute("aria-required");
    var error = "";

    if (ariaRequired && !value) {
      error = "Enter an ".concat(name, ".");
    }

    error = validateField(name, value);
    setFormState(_objectSpread(_objectSpread({}, formState), {}, _defineProperty({}, name, {
      value: value,
      error: error
    })));
  };

  var handleCheckBoxChange = function handleCheckBoxChange(formInput) {
    var _formInput$target2 = formInput.target,
        name = _formInput$target2.name,
        value = _formInput$target2.value;
    var checked = value !== "true";
    setFormState(_objectSpread(_objectSpread({}, formState), {}, _defineProperty({}, name, {
      value: checked,
      error: ""
    })));
  };

  var handleSelectChange = function handleSelectChange(name, selectValue) {
    setFormState(_objectSpread(_objectSpread({}, formState), {}, _defineProperty({}, name, {
      value: selectValue,
      error: ""
    })));
  };

  var handleChangeRadio = function handleChangeRadio(formInput) {
    var _formInput$target3 = formInput.target,
        name = _formInput$target3.name,
        value = _formInput$target3.value;
    setFormState(_objectSpread(_objectSpread({}, formState), {}, _defineProperty({}, name, {
      value: value,
      error: ""
    })));
  };

  var validateForm = function validateForm() {
    var updatedState = _objectSpread({}, formState);

    var isValid = true;
    formFields.forEach(function (field) {
      var error = validateField(field.name, formState[field.name].value);

      if (field.required && !formState[field.name].value) {
        updatedState[field.name].error = field.fieldType === "input" || field.fieldType === "textArea" ? "Enter an ".concat(field.label, ".") || error : "Choose an ".concat(field.label, ".") || error;
        isValid = false;
      } else if (error) {
        updatedState[field.name].error = error;
        isValid = false;
      }
    });
    setFormState(updatedState);
    return isValid;
  };

  var handleClear = function handleClear() {
    var clearedState = formFields.reduce(function (acc, field) {
      acc[field.name] = {
        value: field.fieldType === "checkBox" ? false : "",
        error: ""
      };
      return acc;
    }, {});
    setFormState(clearedState);
    setApiError(null);
  };

  var cascadingDropdownRef = useRef(null);

  var getCascadeDropdownSelectedValues = function getCascadeDropdownSelectedValues() {
    if (cascadingDropdownRef.current) {
      return cascadingDropdownRef.current.getSelectedValues();
    }
  };

  var getFormState = function getFormState() {
    if (!validateForm()) {
      return null;
    }

    var updatedState = _objectSpread(_objectSpread({}, formState), getCascadeDropdownSelectedValues());

    return updatedState;
  };

  useImperativeHandle(ref, function () {
    return {
      getFormState: getFormState,
      handleClear: handleClear,
      setApiError: setApiError
    };
  });

  var renderField = function renderField(data, index) {
    var _data$optionList;

    if (data.fieldType === "radio" && data !== null && data !== void 0 && (_data$optionList = data.optionList) !== null && _data$optionList !== void 0 && _data$optionList.length) {
      data.optionList = data === null || data === void 0 ? void 0 : data.optionList.map(function (option) {
        return _objectSpread(_objectSpread({}, option), {}, {
          onChange: handleChangeRadio
        });
      });
    }

    var filterFn = function filterFn(items, value) {
      if (typeof value !== "string") {
        return [];
      }

      var label = value.toLowerCase();
      return items.filter(function (item) {
        return item.label.toLowerCase().includes(label);
      });
    };

    React.useEffect(function () {
      var element = document.getElementById(data.id);

      if ((data.fieldType === "textArea" || data.fieldType === "dropdown") && element) {
        if (formState[data.name].error) {
          element.classList.add(getClassName({
            descendantName: "descpError"
          }));
        } else {
          element.classList.remove(getClassName({
            descendantName: "descpError"
          }));
        }
      }
    }, [formState[data.name].error]);
    return /*#__PURE__*/React.createElement("div", {
      key: index,
      className: getClassName({
        descendantName: "section"
      })
    }, data.fieldType === "input" && /*#__PURE__*/React.createElement(VerticalSpacing, null, /*#__PURE__*/React.createElement(Form.TextControl, {
      id: data.id,
      labelText: data.required ? "*".concat(data.label) : data.label,
      name: data.name,
      required: data.required,
      value: formState[data.name].value,
      onChange: handleChange,
      isError: Boolean(formState[data.name].error)
    }), Boolean(formState[data.name].error) && /*#__PURE__*/React.createElement("div", {
      className: getClassName({
        descendantName: "errorMsg"
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: getClassName({
        descendantName: "errorIcon"
      })
    }, /*#__PURE__*/React.createElement(ErrorIcon, null)), /*#__PURE__*/React.createElement(Typography, {
      type: Sizes.XXXTRASMALL,
      tag: TypographyTags.P
    }, formState[data.name].error))), data.fieldType === "dropdown" && /*#__PURE__*/React.createElement(VerticalSpacing, null, /*#__PURE__*/React.createElement("label", {
      htmlFor: "filter-input-id-4"
    }, /*#__PURE__*/React.createElement(Typography, {
      className: getClassName({
        descendantName: "dropdownLabel"
      })
    }, data.required ? "*".concat(data.label) : data.label)), /*#__PURE__*/React.createElement(Dropdown, {
      id: data.id,
      filterFunction: filterFn,
      typeaheadMode: true,
      maxVisibleItems: 5,
      block: true,
      items: data.optionList,
      onValueChanged: function onValueChanged(selectValue) {
        return handleSelectChange(data.name, selectValue);
      },
      onClearButtonClick: function onClearButtonClick() {
        return handleSelectChange(data.name, "");
      },
      placeholder: "",
      removeAutoFocus: true
    }), Boolean(formState[data.name].error) && /*#__PURE__*/React.createElement("div", {
      className: getClassName({
        descendantName: "errorMsg"
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: getClassName({
        descendantName: "errorIcon"
      })
    }, /*#__PURE__*/React.createElement(ErrorIcon, null)), /*#__PURE__*/React.createElement(Typography, {
      type: Sizes.XXXTRASMALL,
      tag: TypographyTags.P
    }, formState[data.name].error))), data.fieldType === "checkBox" && /*#__PURE__*/React.createElement(VerticalSpacing, null, /*#__PURE__*/React.createElement(Form.CheckboxControl, {
      id: data.id,
      labelText: data.required ? "*".concat(data.label) : data.label,
      name: data.name,
      required: data.required,
      onChange: handleCheckBoxChange,
      value: formState[data.name].value
    }), Boolean(formState[data.name].error) && /*#__PURE__*/React.createElement("div", {
      className: getClassName({
        descendantName: "errorMsg"
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: getClassName({
        descendantName: "errorIcon"
      })
    }, /*#__PURE__*/React.createElement(ErrorIcon, null)), /*#__PURE__*/React.createElement(Typography, {
      type: Sizes.XXXTRASMALL,
      tag: TypographyTags.P
    }, formState[data.name].error))), data.fieldType === "textArea" && /*#__PURE__*/React.createElement(VerticalSpacing, null, /*#__PURE__*/React.createElement(Form.TextareaControl, {
      id: data.id,
      labelText: data.required ? "*".concat(data.label) : data.label,
      name: data.name,
      required: data.required,
      value: formState[data.name].value,
      onChange: handleChange,
      dynamicClassName: formState[data.name].error ? "Inputerror" : ""
    }), Boolean(formState[data.name].error) && /*#__PURE__*/React.createElement("div", {
      className: getClassName({
        descendantName: "errorMsg"
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: getClassName({
        descendantName: "errorIcon"
      })
    }, /*#__PURE__*/React.createElement(ErrorIcon, null)), /*#__PURE__*/React.createElement(Typography, {
      type: Sizes.XXXTRASMALL,
      tag: TypographyTags.P
    }, formState[data.name].error))), data.fieldType === "radio" && /*#__PURE__*/React.createElement(VerticalSpacing, null, /*#__PURE__*/React.createElement(Form.Fieldset, {
      legend: data.required ? "*".concat(data.label) : data.label,
      id: data.id,
      onChange: handleChangeRadio
    }, /*#__PURE__*/React.createElement(Form.RadioControl, {
      name: data.name,
      required: data.required,
      options: data.optionList,
      disabled: false
    })), Boolean(formState[data.name].error) && /*#__PURE__*/React.createElement("div", {
      className: getClassName({
        descendantName: "errorMsg"
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: getClassName({
        descendantName: "errorIcon"
      })
    }, /*#__PURE__*/React.createElement(ErrorIcon, null)), /*#__PURE__*/React.createElement(Typography, {
      type: Sizes.XXXTRASMALL,
      tag: TypographyTags.P
    }, formState[data.name].error))), data.fieldType === "dependentDropdown" && /*#__PURE__*/React.createElement(VerticalSpacing, null, /*#__PURE__*/React.createElement(CascadingDropdown, {
      ref: cascadingDropdownRef,
      dependentDropdowns: data.dependentDropdowns,
      columns: data.columns
    })));
  };

  return /*#__PURE__*/React.createElement("div", _extends({
    className: getClassName(),
    "data-rehydratable": getRehydratableName(DynamicForm.displayName)
  }, propsToDataAttributes(props)), apiError && /*#__PURE__*/React.createElement(Toast, {
    type: ToastType.WARNING,
    closeButton: false
  }, apiError), /*#__PURE__*/React.createElement("div", {
    className: getClassName({
      descendantName: "required"
    })
  }, /*#__PURE__*/React.createElement("p", null, REQUIRED)), formFields === null || formFields === void 0 ? void 0 : formFields.map(renderField));
});
DynamicForm.displayName = "DynamicForm";